/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Manage assets", description = "Give Ad Studio and Lifecycle Studio the images, templates, video, and product data they build from. Organize files into collections, sync them from your ad platforms and DAM, and connect product catalogs.", lastUpdatedDate = new Date(1792540800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Manage assets",
        description: "Give Ad Studio and Lifecycle Studio the images, templates, video, and product data they build from. Organize files into collections, sync them from your ad platforms and DAM, and connect product catalogs.",
        lastUpdatedDate: "Wed Oct 21 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Organize assets into collections",
          "depth": 0,
          "id": "organize-assets-into-collections"
        }, {
          "value": "Add collection instructions",
          "depth": 1,
          "id": "add-collection-instructions"
        }, {
          "value": "Limit a collection to specific brands",
          "depth": 1,
          "id": "limit-a-collection-to-specific-brands"
        }, {
          "value": "Control when assets load",
          "depth": 1,
          "id": "control-when-assets-load"
        }, {
          "value": "Control asset modifications",
          "depth": 1,
          "id": "control-asset-modifications"
        }, {
          "value": "Upload assets",
          "depth": 0,
          "id": "upload-assets"
        }, {
          "value": "Upload Figma templates",
          "depth": 1,
          "id": "upload-figma-templates"
        }, {
          "value": "Sync assets from connected platforms",
          "depth": 0,
          "id": "sync-assets-from-connected-platforms"
        }, {
          "value": "Ads from your ad platforms",
          "depth": 1,
          "id": "ads-from-your-ad-platforms"
        }, {
          "value": "Files from your digital asset manager",
          "depth": 1,
          "id": "files-from-your-digital-asset-manager"
        }, {
          "value": "Connect a product catalog",
          "depth": 0,
          "id": "connect-a-product-catalog"
        }, {
          "value": "Upload a product file",
          "depth": 1,
          "id": "upload-a-product-file"
        }, {
          "value": "Connect a product feed",
          "depth": 1,
          "id": "connect-a-product-feed"
        }, {
          "value": "Reference assets in a prompt",
          "depth": 0,
          "id": "reference-assets-in-a-prompt"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      ul: "ul",
      li: "li",
      img: "img",
      h3: "h3",
      ol: "ol",
      code: "code"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketing teams, creative teams, platform admins"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: ["The ", _jsxs(_components.a, {
                    href: "/workspace-management/roles#agents",
                    children: [_jsx(_components.strong, {
                      children: "Configure Agents"
                    }), " grant"]
                  }), "."]
                }), _jsxs("li", {
                  children: [_jsx(_components.a, {
                    href: "/ad-studio/overview",
                    children: "Ad Studio"
                  }), " or ", _jsx(_components.a, {
                    href: "/lifecycle-studio/overview",
                    children: "Lifecycle Studio"
                  }), "."]
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Manage the images, templates, videos, and product catalogs agents use to generate content."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Ad Studio and Lifecycle Studio share one asset library in ", _jsx(_components.strong, {
          children: "Context Hub > Assets"
        }), ", so you add a file once and both products can use it. Files live in collections, grouped by where they come from:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Uploaded"
          }), " — Collections you fill yourself, by uploading files or importing them from a spreadsheet."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Synced"
          }), " — Collections that Hightouch fills from a connected source, such as ads from your ad platforms or files from your digital asset manager (DAM). You can't upload files to them."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Product catalog"
          }), " — Your product data, uploaded from a file or synced from a product feed."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Agents use your prompts, collection instructions, and brand settings to find relevant assets. You can also ", _jsx(_components.a, {
          href: "#reference-assets-in-a-prompt",
          children: "reference a specific asset in a prompt"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Assets are the files agents place in content. Each brand's identity, including its logos, colors, and typefaces, belongs in its ", _jsx(_components.a, {
          href: "/context-hub/brands",
          children: "brand kit"
        }), ". Logo files and lockups you want agents to place in generated content can also go in a collection here. Business knowledge and instructions belong in ", _jsx(_components.a, {
          href: "/context-hub/business-context",
          children: "Knowledge"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-assets-product-catalog.png",
          alt: "Assets page with the Uploaded, Synced, and Product catalog sections and a product catalog open"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "organize-assets-into-collections",
        children: "Organize assets into collections"
      }), "\n", _jsx(_components.p, {
        children: "Create a collection for each group of files the agent should handle the same way. Product photography, logos, templates, and lifestyle imagery each need different instructions, so give each its own collection."
      }), "\n", _jsxs(_components.p, {
        children: ["To create one, select ", _jsx(_components.strong, {
          children: "Add collection"
        }), " in the ", _jsx(_components.strong, {
          children: "Uploaded"
        }), " section. When you set up a collection, you decide:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "What to call it, and whether it holds only images, only components, only video, or any kind of asset."
        }), "\n", _jsx(_components.li, {
          children: "How the agent should use the files."
        }), "\n", _jsx(_components.li, {
          children: "Which brands can use them."
        }), "\n", _jsx(_components.li, {
          children: "Whether every agent session includes them."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["To change these settings later, open the collection's actions menu and select ", _jsx(_components.strong, {
          children: "Edit collection"
        }), ". Synced collections use the same instructions, brand, and loading settings, but ", _jsx(_components.a, {
          href: "#sync-assets-from-connected-platforms",
          children: "their files come from the connected source"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-assets-new-collection-dialog.png",
          alt: "New collection dialog with Name, Asset type, Instructions, Brand profiles, and Always load into context"
        })
      }), "\n", _jsx(_components.h3, {
        id: "add-collection-instructions",
        children: "Add collection instructions"
      }), "\n", _jsxs(_components.p, {
        children: ["In ", _jsx(_components.strong, {
          children: "Instructions"
        }), ", answer the question the field asks: \"How should the agent use assets in this collection?\" Write it the way you'd brief a designer who hasn't seen your ads before. For example:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "\"Use these only as background photography.\""
        }), "\n", _jsx(_components.li, {
          children: "\"Always place the logo in a corner, never over a product.\""
        }), "\n", _jsx(_components.li, {
          children: "\"Use these lifestyle shots for spring campaigns only.\""
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Instructions apply to every file in the collection, so write a rule here once instead of repeating it in every prompt."
      }), "\n", _jsx(_components.h3, {
        id: "limit-a-collection-to-specific-brands",
        children: "Limit a collection to specific brands"
      }), "\n", _jsxs(_components.p, {
        children: ["Limit a collection when its files belong to one brand, so they stay out of your other brands' ads and emails. In ", _jsx(_components.strong, {
          children: "Brand profiles"
        }), ", select the brands that can use the collection. Leave it empty to apply the collection to every brand profile."]
      }), "\n", _jsx(_components.h3, {
        id: "control-when-assets-load",
        children: "Control when assets load"
      }), "\n", _jsxs(_components.p, {
        children: ["Turn on ", _jsx(_components.strong, {
          children: "Always load into context"
        }), " for the few files the agent should see every time, such as your primary logos and brand lockups. Every asset in the collection then goes into agent sessions as reference material."]
      }), "\n", _jsx(_components.p, {
        children: "Leave it off for large collections like product photography or campaign imagery. Agents still find files in those collections based on your prompt and the collection's instructions."
      }), "\n", _jsx(_components.h3, {
        id: "control-asset-modifications",
        children: "Control asset modifications"
      }), "\n", _jsxs(_components.p, {
        children: ["Turn off AI edits for a collection when its files, such as licensed photos or approved product shots, must be used exactly as they are. You set this in ", _jsx(_components.a, {
          href: "/context-hub/guardrails#limit-changes-for-specific-asset-collections",
          children: "Guardrails"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "upload-assets",
        children: "Upload assets"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Context Hub > Assets"
          }), " and open a collection in the ", _jsx(_components.strong, {
            children: "Uploaded"
          }), " section."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Upload"
          }), ", or drag files onto the collection."]
        }), "\n", _jsxs(_components.li, {
          children: ["Choose how to upload:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Files"
              }), " — Add image, component, or video files directly."]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Spreadsheet"
              }), " — Import assets from a CSV, XML, or JSONL file that lists them by URL. Then map the columns, such as ", _jsx(_components.strong, {
                children: "Record ID column"
              }), ", ", _jsx(_components.strong, {
                children: "Title column"
              }), ", and the column that holds each image or video URL."]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Upload"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-assets-upload-files-dialog.png",
          alt: "Upload files dialog with the Files and Spreadsheet options and one file ready to upload"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["A collection accepts only the file types that match its ", _jsx(_components.strong, {
          children: "Asset type"
        }), ". Images can be JPG, PNG, GIF, or WebP, components are SVG, and video can be MP4, MOV, or WebM."]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Upload a range of material, such as product shots, lifestyle imagery, logos in several formats, and background textures, so agents have more to choose from when they generate different concepts."
        })
      }), "\n", _jsx(_components.h3, {
        id: "upload-figma-templates",
        children: "Upload Figma templates"
      }), "\n", _jsxs(_components.p, {
        children: ["To generate ads against your own Figma layouts, export each template frame as SVG and upload it to a collection with the ", _jsx(_components.strong, {
          children: "Component"
        }), " asset type. In Figma's export settings, leave ", _jsx(_components.strong, {
          children: "Outline text"
        }), " unchecked. That keeps the text layers editable, so the agent can replace copy in the template instead of redrawing it."]
      }), "\n", _jsxs(_components.p, {
        children: ["To import Figma frames as templates without exporting them, ", _jsx(_components.a, {
          href: "/context-hub/connections#connect-figma-designs",
          children: "connect Figma Designs"
        }), " in Connected data."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "sync-assets-from-connected-platforms",
        children: "Sync assets from connected platforms"
      }), "\n", _jsxs(_components.p, {
        children: ["Use synced collections for files that already live in your ad platforms or DAM. Hightouch fills them from the connected source, so you don't upload or replace those files by hand. You can still edit a synced collection's instructions, brand profiles, and ", _jsx(_components.strong, {
          children: "Always load into context"
        }), " setting."]
      }), "\n", _jsx(_components.h3, {
        id: "ads-from-your-ad-platforms",
        children: "Ads from your ad platforms"
      }), "\n", _jsxs(_components.p, {
        children: ["When you ", _jsx(_components.a, {
          href: "/context-hub/connections#connect-advertising-and-lifecycle-platforms",
          children: "connect Facebook Ads"
        }), ", Hightouch syncs your static and video ads into the ", _jsx(_components.strong, {
          children: "Static Ads"
        }), " and ", _jsx(_components.strong, {
          children: "Video Ads"
        }), " collections in the ", _jsx(_components.strong, {
          children: "Synced"
        }), " section once a day. Ads that stop appearing in your account are removed after 60 days. These collections are named by the sync and can't be renamed."]
      }), "\n", _jsx(_components.p, {
        children: "Agents use these ads as inspiration for mood, palette, and composition. They don't copy an ad's layout or copy unless you ask them to keep specific elements."
      }), "\n", _jsx(_components.h3, {
        id: "files-from-your-digital-asset-manager",
        children: "Files from your digital asset manager"
      }), "\n", _jsxs(_components.p, {
        children: ["To bring files in from your DAM, first connect it in ", _jsx(_components.a, {
          href: "/context-hub/connections#digital-asset-managers",
          children: "Connected data"
        }), ". Syncs work with the digital asset managers listed there, such as Bynder, Canto, and Frontify. The Figma cards don't support synced collections."]
      }), "\n", _jsx(_components.p, {
        children: "After you connect your DAM:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In the ", _jsx(_components.strong, {
            children: "Synced"
          }), " section, select ", _jsx(_components.strong, {
            children: "Create synced collection"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Name the collection, add instructions, and select brand profiles, then select ", _jsx(_components.strong, {
            children: "Add"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["In the new collection, select ", _jsx(_components.strong, {
            children: "Create sync"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "Source"
          }), ", name the sync, and choose which part of your DAM library to pull from."]
        }), "\n", _jsx(_components.li, {
          children: "Set a schedule: run the sync manually, or keep it current on an interval, a custom recurrence, or a cron expression."
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Create sync"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-assets-new-synced-collection-dialog.png",
          alt: "New synced collection dialog with Name, Instructions, Brand profiles, and Always load into context"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["To check a sync's progress, run it again, or change its settings, open the collection's actions menu and select ", _jsx(_components.strong, {
          children: "Manage sync"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "connect-a-product-catalog",
        children: "Connect a product catalog"
      }), "\n", _jsx(_components.p, {
        children: "Connect a product catalog when you want agents to generate product-specific ads and emails. The catalog gives agents your product names, descriptions, and images during generation. If your team creates brand or campaign content rather than product content, you can skip it."
      }), "\n", _jsxs(_components.p, {
        children: ["To add a catalog, select ", _jsx(_components.strong, {
          children: "Create product catalog"
        }), " in the ", _jsx(_components.strong, {
          children: "Product catalog"
        }), " section, enter a ", _jsx(_components.strong, {
          children: "Name"
        }), ", and select a ", _jsx(_components.strong, {
          children: "Brand profile"
        }), " or ", _jsx(_components.strong, {
          children: "All brands"
        }), ". You can add more than one, such as one per brand."]
      }), "\n", _jsxs(_components.p, {
        children: ["To fill a catalog, open it and select ", _jsx(_components.strong, {
          children: "Add products"
        }), ". Choose how products arrive:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Upload a file"
          }), " — A one-time import. Use it for a small or rarely changing catalog, or to try the catalog before you set up a feed."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Connect a product feed"
          }), " — A recurring sync from a hosted feed. Use it when your products, prices, or images change often."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-assets-add-products-dialog.png",
          alt: "Add products dialog with Upload a file and Connect a product feed"
        })
      }), "\n", _jsx(_components.h3, {
        id: "upload-a-product-file",
        children: "Upload a product file"
      }), "\n", _jsxs(_components.p, {
        children: ["Upload a CSV, XML, or JSONL file of up to 1 GB, choose its ", _jsx(_components.strong, {
          children: "Format"
        }), ", then map your columns to the catalog fields:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Field"
            }), _jsx(_components.th, {
              children: "Required"
            }), _jsx(_components.th, {
              children: "How the agent uses it"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Record ID column"
              })
            }), _jsx(_components.td, {
              children: "Yes"
            }), _jsx(_components.td, {
              children: "The primary key, or SKU, that identifies each product"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Title column"
              })
            }), _jsx(_components.td, {
              children: "Yes"
            }), _jsx(_components.td, {
              children: "Product name in headlines and copy"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Primary image column"
              })
            }), _jsx(_components.td, {
              children: "Yes"
            }), _jsx(_components.td, {
              children: "URL of the main product image used in generated content"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Description column"
              })
            }), _jsx(_components.td, {
              children: "No"
            }), _jsx(_components.td, {
              children: "Product details that inform copy"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Additional image columns"
              })
            }), _jsx(_components.td, {
              children: "No"
            }), _jsx(_components.td, {
              children: "URLs of extra product images, for more variety"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-upload-products-dialog.png",
          alt: "Upload products to catalog dialog with Catalog file, Format, Record ID column, and Title column"
        })
      }), "\n", _jsx(_components.h3, {
        id: "connect-a-product-feed",
        children: "Connect a product feed"
      }), "\n", _jsx(_components.p, {
        children: "Connect a feed when you already publish a product feed file, such as a Shopify or Google Merchant export, and want the catalog to stay in sync with it."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Enter a ", _jsx(_components.strong, {
            children: "Name"
          }), " for the feed."]
        }), "\n", _jsxs(_components.li, {
          children: ["Choose where the feed lives in ", _jsx(_components.strong, {
            children: "Source type"
          }), ":", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "File URL"
              }), " — A public or authenticated URL to the feed file. Add request headers, such as an authorization header, if the URL needs them."]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "SFTP"
              }), " — A file on your SFTP server, using a password or private key."]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["Choose the feed's ", _jsx(_components.strong, {
            children: "Format"
          }), ": CSV, TSV, or JSONL."]
        }), "\n", _jsxs(_components.li, {
          children: ["Map the feed columns:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Google Merchant preset"
              }), " — Use it when your feed follows the standard Google Merchant columns, such as ", _jsx(_components.code, {
                children: "id"
              }), ", ", _jsx(_components.code, {
                children: "title"
              }), ", and ", _jsx(_components.code, {
                children: "image_link"
              }), "."]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Custom mapping"
              }), " — Name the feed column for each product field when your feed uses its own column names."]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["Choose a ", _jsx(_components.strong, {
            children: "Schedule type"
          }), ":", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Manual"
              }), " — Sync only when you trigger it. Good for a catalog that rarely changes."]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Interval"
              }), " — Sync at a regular interval, such as every few hours."]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Custom recurrence"
              }), " — Sync on specific days and times."]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Cron expression"
              }), " — Sync on a schedule you write as a cron expression."]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Add feed"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-add-product-feed-panel.png",
          alt: "Add product feed panel with Name, Source type, Catalog source, Format, Field mapping, and Schedule type"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["To run a feed now, view its sync runs, or edit it, open the catalog's actions menu and select ", _jsx(_components.strong, {
          children: "Manage sync"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["To use catalog products in generated content, see ", _jsx(_components.a, {
          href: "/ad-studio/ads#choose-a-product",
          children: "Ads"
        }), " and ", _jsx(_components.a, {
          href: "/lifecycle-studio/campaigns",
          children: "Lifecycle Studio campaigns"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "reference-assets-in-a-prompt",
        children: "Reference assets in a prompt"
      }), "\n", _jsx(_components.p, {
        children: "Name an asset in your prompt when you want a specific file in the result instead of the one the agent would choose. Refer to it exactly or loosely, such as \"the blue sweater,\" and say whether to use it as-is or as a starting point."
      }), "\n", _jsxs(_components.p, {
        children: ["During iteration you can swap assets in the chat, such as \"use the lifestyle shot instead of the product image,\" or select a specific file in the ", _jsx(_components.a, {
          href: "/ad-studio/refine#edit-with-the-visual-editor",
          children: "visual editor"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "/ad-studio/prompting-guide#reuse-assets-from-your-workspace",
          children: "Reuse assets from your workspace"
        }), " for how to word these requests."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/context-hub/connections#digital-asset-managers",
            children: "Connect a digital asset manager"
          }), " to bring your DAM files into a synced collection."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/context-hub/guardrails#limit-changes-for-specific-asset-collections",
            children: "Set guardrails"
          }), " to control which changes agents can make to each collection's images."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
